/**
 * API client for communicating with PromptMySite backend
 */

import type { OrchestrateRequest, OrchestrateResponse, WorkspaceConfig } from './config';
import { DEFAULT_REQUEST_TIMEOUT_MS, HEALTH_CHECK_TIMEOUT_MS } from './constants';
import { extractErrorFromResponse } from './error-utils';
import { createLogger } from './logger';

const log = createLogger('ApiClient');

/**
 * Summary of a workspace for listing
 */
export interface WorkspaceSummary {
  id: string;
  name: string;
  tagline?: string;
  accentColor?: string;
}

export class ApiClient {
  private baseUrl: string;
  private abortController: AbortController | null = null;
  private apiKey: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/$/, ''); // Remove trailing slash
  }

  /**
   * Set the API key for authenticated requests
   */
  setApiKey(apiKey: string | null): void {
    this.apiKey = apiKey;
  }

  /**
   * Get the currently set API key
   */
  getApiKey(): string | null {
    return this.apiKey;
  }

  /**
   * Get the base API URL
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Build headers for requests
   */
  private buildHeaders(method: 'GET' | 'POST' = 'POST'): Record<string, string> {
    const headers: Record<string, string> = {};

    // Only set Content-Type for requests with a body (POST/PUT/PATCH).
    // Setting it on GET triggers an unnecessary CORS preflight because
    // 'application/json' is not a CORS-safelisted content type.
    if (method !== 'GET') {
      headers['Content-Type'] = 'application/json';
    }

    if (this.apiKey) {
      headers['X-Api-Key'] = this.apiKey;
    }

    return headers;
  }

  /**
   * Fetch with automatic timeout
   * @param url The URL to fetch
   * @param options Fetch options
   * @param timeoutMs Timeout in milliseconds (default: 30s)
   */
  private async fetchWithTimeout(
    url: string,
    options: RequestInit = {},
    timeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      return response;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Send a message to the orchestrate endpoint
   */
  async orchestrate(
    workspaceId: string,
    sessionId: string,
    message: string,
    timeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS
  ): Promise<OrchestrateResponse> {
    // Cancel any pending request
    this.abort();
    this.abortController = new AbortController();

    const request: OrchestrateRequest = {
      workspaceId,
      sessionId,
      message,
    };

    // Set up timeout
    const timeoutId = setTimeout(() => this.abortController?.abort(), timeoutMs);

    try {
      const response = await fetch(`${this.baseUrl}/api/orchestrate`, {
        method: 'POST',
        headers: this.buildHeaders(),
        body: JSON.stringify(request),
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        const { errorMessage, errorCode } = await extractErrorFromResponse(response);
        throw new ApiError(errorMessage, response.status, errorCode);
      }

      const data = await response.json() as OrchestrateResponse;
      return data;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError('Request timed out or was cancelled', 0);
      }
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        error instanceof Error ? error.message : 'Network error',
        0
      );
    } finally {
      clearTimeout(timeoutId);
      this.abortController = null;
    }
  }

  /**
   * Fetch workspace configuration
   */
  async getWorkspace(workspaceId: string): Promise<WorkspaceConfig> {
    try {
      const response = await this.fetchWithTimeout(
        `${this.baseUrl}/api/workspaces/${workspaceId}`,
        {
          method: 'GET',
          headers: this.buildHeaders('GET'),
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new ApiError('Workspace not found', 404);
        }
        const { errorMessage, errorCode } = await extractErrorFromResponse(response);
        throw new ApiError(errorMessage, response.status, errorCode);
      }

      const data = await response.json() as WorkspaceConfig;
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        error instanceof Error ? error.message : 'Network error',
        0
      );
    }
  }

  /**
   * List all available workspaces
   */
  async listWorkspaces(): Promise<WorkspaceSummary[]> {
    try {
      const response = await this.fetchWithTimeout(
        `${this.baseUrl}/api/workspaces`,
        {
          method: 'GET',
          headers: this.buildHeaders('GET'),
        }
      );

      if (!response.ok) {
        const { errorMessage } = await extractErrorFromResponse(response);
        throw new ApiError(errorMessage, response.status);
      }

      const data = await response.json() as WorkspaceSummary[];
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        error instanceof Error ? error.message : 'Network error',
        0
      );
    }
  }

  /**
   * Check if the backend has an API key configured
   * Returns true if key is configured, false if client needs to provide one
   */
  async checkApiKeyStatus(): Promise<boolean> {
    try {
      const response = await this.fetchWithTimeout(
        `${this.baseUrl}/api/health`,
        {
          method: 'GET',
          headers: this.buildHeaders('GET'),
        },
        HEALTH_CHECK_TIMEOUT_MS
      );

      if (!response.ok) {
        return false;
      }

      const data = await response.json() as { apiKeyConfigured?: boolean };
      return data.apiKeyConfigured !== false;
    } catch {
      return false;
    }
  }

  /**
   * Abort any pending request
   */
  abort(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Submit feedback for a message
   */
  async submitFeedback(
    messageId: string,
    sessionId: string,
    rating: 'positive' | 'negative',
    comment?: string
  ): Promise<void> {
    try {
      const response = await this.fetchWithTimeout(
        `${this.baseUrl}/api/feedback`,
        {
          method: 'POST',
          headers: this.buildHeaders(),
          body: JSON.stringify({
            messageId,
            sessionId,
            rating,
            comment
          }),
        }
      );

      if (!response.ok) {
        log.warn('Failed to submit feedback:', response.status);
      }
    } catch (error) {
      log.warn('Failed to submit feedback:', error);
    }
  }

  /**
   * Fetch live section data for data-driven UI blocks.
   * This powers the live-section blocks with dynamic content.
   *
   * @param workspaceId the workspace ID
   * @param sectionId the section ID (e.g., "players", "products")
   * @param filters optional filter key-value pairs
   * @param limit maximum items to return
   */
  async getLiveSectionData(
    workspaceId: string,
    sectionId: string,
    filters?: Record<string, string>,
    limit = 20
  ): Promise<LiveSectionResponse> {
    try {
      // Build query string with filters
      const params = new URLSearchParams({ limit: String(limit) });
      if (filters) {
        for (const [key, value] of Object.entries(filters)) {
          if (value) {
            params.append(key, value);
          }
        }
      }

      const response = await this.fetchWithTimeout(
        `${this.baseUrl}/api/workspaces/${workspaceId}/sections/${sectionId}/data?${params}`,
        {
          method: 'GET',
          headers: this.buildHeaders('GET'),
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          // Return empty data if section doesn't exist yet
          return {
            sectionId,
            items: [],
            filters: [],
            totalCount: 0
          };
        }
        throw new ApiError(`Failed to fetch section data: ${response.status}`, response.status);
      }

      return await response.json() as LiveSectionResponse;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      log.warn('Failed to fetch live section data:', error);
      // Return empty response on error for graceful degradation
      return {
        sectionId,
        items: [],
        filters: [],
        totalCount: 0
      };
    }
  }

  /**
   * Search items within a live section.
   */
  async searchLiveSection(
    workspaceId: string,
    sectionId: string,
    query: string
  ): Promise<LiveDataItem[]> {
    try {
      const response = await this.fetchWithTimeout(
        `${this.baseUrl}/api/workspaces/${workspaceId}/sections/${sectionId}/search?q=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: this.buildHeaders('GET'),
        }
      );

      if (!response.ok) {
        return [];
      }

      return await response.json() as LiveDataItem[];
    } catch (error) {
      log.warn('Failed to search live section:', error);
      return [];
    }
  }

  /**
   * Fetch a flow step group for rendering a flow form.
   *
   * @param workspaceId the workspace ID
   * @param flowId the flow ID
   * @param groupIndex the step group index (0-based)
   * @param lang optional target language code for label translation
   */
  async getFlowStepGroup(
    workspaceId: string,
    flowId: string,
    groupIndex: number,
    lang?: string
  ): Promise<FlowStepGroup | null> {
    try {
      const url = new URL(
        `${this.baseUrl}/api/workspaces/${workspaceId}/flows/${flowId}/groups/${groupIndex}`,
        // Provide a base for relative URLs (shouldn't happen, but defensive)
        typeof window !== 'undefined' ? window.location.origin : undefined
      );
      if (lang) {
        url.searchParams.set('lang', lang);
      }

      const response = await this.fetchWithTimeout(
        url.toString(),
        {
          method: 'GET',
          headers: this.buildHeaders('GET'),
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new ApiError(`Failed to fetch flow step group: ${response.status}`, response.status);
      }

      return await response.json() as FlowStepGroup;
    } catch (error) {
      log.warn('Failed to fetch flow step group:', error);
      return null;
    }
  }

  /**
   * Submit a flow form for capture (stored in DB, optional webhook).
   * Fire-and-forget — errors are logged but never block the user.
   */
  async submitFlowForm(
    workspaceId: string,
    data: {
      sessionId: string;
      flowId: string;
      flowName: string;
      data: Record<string, string>;
    }
  ): Promise<{ id: string; status: string } | null> {
    try {
      const response = await this.fetchWithTimeout(
        `${this.baseUrl}/api/workspaces/${workspaceId}/flow-submissions`,
        {
          method: 'POST',
          headers: this.buildHeaders(),
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        log.warn('Flow submission capture failed:', response.status);
        return null;
      }

      return await response.json() as { id: string; status: string };
    } catch (error) {
      log.warn('Flow submission capture failed:', error);
      return null;
    }
  }

  /**
   * Match an uploaded CV/resume against job listings for a workspace.
   * Sends the file as base64 and returns ranked job matches.
   *
   * @param workspaceId the workspace ID
   * @param fileName original file name (used for content type detection)
   * @param fileContent base64-encoded file content
   */
  async matchResume(
    workspaceId: string,
    fileName: string,
    fileContent: string
  ): Promise<ResumeMatchResult | null> {
    try {
      const response = await this.fetchWithTimeout(
        `${this.baseUrl}/api/workspaces/${workspaceId}/resume-match`,
        {
          method: 'POST',
          headers: this.buildHeaders(),
          body: JSON.stringify({ fileName, fileContent }),
        }
      );
      if (!response.ok) {
        log.warn('Resume matching failed:', response.status);
        return null;
      }
      return await response.json() as ResumeMatchResult;
    } catch (error) {
      log.warn('Resume matching failed:', error);
      return null;
    }
  }

  /**
   * Fetch a complete flow definition.
   *
   * @param workspaceId the workspace ID
   * @param flowId the flow ID
   */
  async getFlowDefinition(
    workspaceId: string,
    flowId: string
  ): Promise<FlowDefinition | null> {
    try {
      const response = await this.fetchWithTimeout(
        `${this.baseUrl}/api/workspaces/${workspaceId}/flows/${flowId}`,
        {
          method: 'GET',
          headers: this.buildHeaders('GET'),
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new ApiError(`Failed to fetch flow: ${response.status}`, response.status);
      }

      return await response.json() as FlowDefinition;
    } catch (error) {
      log.warn('Failed to fetch flow definition:', error);
      return null;
    }
  }
}

/**
 * Live data item from the backend
 */
export interface LiveDataItem {
  id: string;
  sectionId: string;
  title: string;
  description?: string;
  imageUrl?: string;
  badge?: string;
  price?: string;
  category?: string;
  subcategory?: string;
  metadata?: Record<string, unknown>;
  filterValues?: Record<string, string>;
  sortOrder?: number;
  active?: boolean;
  actionConfig?: {
    label?: string;
    value?: string;
    url?: string;
  };
}

/**
 * Live section response from the backend
 */
export interface LiveSectionResponse {
  sectionId: string;
  items: LiveDataItem[];
  filters: LiveSectionFilter[];
  totalCount: number;
}

/**
 * Filter definition for a live section
 */
export interface LiveSectionFilter {
  id: string;
  label: string;
  type: string;
  options: { value: string; label: string }[];
}

/**
 * Result of a resume matching operation
 */
export interface ResumeMatchResult {
  matches: JobMatch[];
  candidateSummary: string;
  noMatchMessage: string | null;
}

/**
 * A single job match with score and match reasoning
 */
export interface JobMatch {
  jobId: string;
  title: string;
  description: string;
  badge: string | null;
  price: string | null;
  category: string | null;
  location: string | null;
  score: number;
  matchReason: string;
}

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  public statusCode: number;
  public errorCode?: string;

  constructor(message: string, statusCode: number, errorCode?: string) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.errorCode = errorCode;
  }

  isNetworkError(): boolean {
    return this.statusCode === 0;
  }

  isNotFound(): boolean {
    return this.statusCode === 404;
  }

  isRateLimited(): boolean {
    return this.statusCode === 429;
  }

  isServerError(): boolean {
    return this.statusCode >= 500;
  }

  isUnauthorized(): boolean {
    return this.statusCode === 401;
  }

  /**
   * Check if the error indicates an API key is required
   */
  isApiKeyRequired(): boolean {
    return this.statusCode === 401 &&
      (this.errorCode === 'API_KEY_REQUIRED' ||
       this.message.toLowerCase().includes('api key'));
  }

  /**
   * Check if the error indicates an invalid API key
   */
  isInvalidApiKey(): boolean {
    return this.statusCode === 401 &&
      (this.errorCode === 'INVALID_API_KEY' ||
       this.message.toLowerCase().includes('invalid'));
  }
}

/**
 * Create a singleton API client instance
 */
let clientInstance: ApiClient | null = null;

export function getApiClient(baseUrl?: string): ApiClient {
  if (!clientInstance && baseUrl) {
    clientInstance = new ApiClient(baseUrl);
  }
  if (!clientInstance) {
    throw new Error('API client not initialized. Provide baseUrl on first call.');
  }
  return clientInstance;
}

export function initApiClient(baseUrl: string): ApiClient {
  clientInstance = new ApiClient(baseUrl);
  return clientInstance;
}

// =============================================================================
// Flow Types
// =============================================================================

/**
 * A flow definition for multi-step forms.
 */
export interface FlowDefinition {
  id: string;
  name: string;
  description?: string;
  triggerPhrases?: string[];
  stepGroups: FlowStepGroup[];
}

/**
 * A group of fields presented together in a flow step.
 */
export interface FlowStepGroup {
  index: number;
  title: string;
  description?: string;
  fields: FlowField[];
  submitLabel?: string;
}

/**
 * A field within a flow step group.
 */
export interface FlowField {
  id: string;
  type: 'cards' | 'select' | 'multiselect' | 'slider' | 'text' | 'email' | 'tel' | 'textarea' | 'date' | 'time' | 'file';
  label?: string;
  prompt?: string;
  options?: FlowFieldOption[];
  slider?: SliderConfig;
  /** File upload config (for type='file') */
  fileConfig?: {
    accept?: string;
    maxSizeMb?: number;
  };
}

/**
 * An option for select/multiselect/cards fields.
 */
export interface FlowFieldOption {
  id: string;
  label: string;
  description?: string;
  image?: string;
}

/**
 * Configuration for slider fields.
 */
export interface SliderConfig {
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  prefix?: string;
  suffix?: string;
  labels?: Record<number, string>;
}
